import React from 'react';
import { Bot } from '@/types/Bot';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { 
  Star, ExternalLink, X, CheckCircle2, XCircle, Sparkles, 
  DollarSign, Users, Trophy, TrendingUp, Zap, ArrowRight
} from 'lucide-react';
import { motion } from 'framer-motion';

interface CompareProps {
  comparisonList: Bot[];
  onRemoveFromComparison: (botId: string) => void;
  onClearComparison: () => void;
}

export const Compare: React.FC<CompareProps> = ({
  comparisonList,
  onRemoveFromComparison,
  onClearComparison,
}) => {
  if (comparisonList.length === 0) {
    return (
      <div className="container mx-auto px-4 py-24">
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="max-w-lg mx-auto text-center"
        >
          <div className="w-20 h-20 mx-auto rounded-2xl bg-primary flex items-center justify-center mb-6">
            <Sparkles className="w-10 h-10 text-primary-foreground" />
          </div>
          <h2 className="text-3xl font-bold mb-3">No Bots to Compare</h2>
          <p className="text-muted-foreground text-lg mb-8">
            Add at least 2 bots from the main page to start comparing.
          </p>
          <Button onClick={() => window.location.href = '/'} size="lg" className="bg-primary">
            <ArrowRight className="w-4 h-4 mr-2" />
            Browse AI Bots
          </Button>
        </motion.div>
      </div>
    );
  }

  const bestRatedBot = comparisonList.reduce((best, bot) => 
    bot.rating > best.rating ? bot : best, comparisonList[0]);

  const colCount = comparisonList.length;

  // Collect all unique features across bots
  const allFeatures = Array.from(new Set(
    comparisonList.flatMap(bot => bot.key_features || [])
  ));

  return (
    <div className="container mx-auto px-4 py-8 max-w-7xl">
      {/* Header */}
      <motion.div 
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        className="mb-8 flex items-center justify-between flex-wrap gap-4"
      >
        <div>
          <h1 className="text-3xl font-bold text-foreground mb-1">Side-by-Side Comparison</h1>
          <p className="text-muted-foreground">
            Comparing {comparisonList.length} AI assistant{comparisonList.length > 1 ? 's' : ''}
          </p>
        </div>
        <Button variant="outline" onClick={onClearComparison} size="sm">
          <X className="w-4 h-4 mr-2" />
          Clear All
        </Button>
      </motion.div>

      {/* Side-by-side comparison table */}
      <div className="overflow-x-auto">
        <div className="min-w-[600px]">
          {/* Bot Headers */}
          <div className="grid gap-4 mb-6" style={{ gridTemplateColumns: `180px repeat(${colCount}, 1fr)` }}>
            <div /> {/* Label column spacer */}
            {comparisonList.map((bot, i) => (
              <motion.div
                key={bot.id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.1 }}
              >
                <Card className="relative p-5 bg-card border-border text-center">
                  <Button
                    variant="ghost"
                    size="icon"
                    className="absolute top-2 right-2 h-7 w-7 rounded-full hover:bg-destructive/10 hover:text-destructive"
                    onClick={() => onRemoveFromComparison(bot.id)}
                  >
                    <X className="w-3.5 h-3.5" />
                  </Button>
                  {bot.id === bestRatedBot.id && comparisonList.length > 1 && (
                    <Badge className="absolute top-2 left-2 bg-yellow-500/15 text-yellow-600 dark:text-yellow-400 border-yellow-500/30 text-[10px]">
                      <Trophy className="w-3 h-3 mr-1" />
                      Top
                    </Badge>
                  )}
                  <div className="pt-4">
                    {bot.imageUrl || bot.thumbnail ? (
                      <img src={bot.imageUrl || bot.thumbnail} alt={bot.name} className="w-14 h-14 rounded-xl object-cover mx-auto mb-3 shadow-sm" />
                    ) : (
                      <div className="w-14 h-14 rounded-xl bg-muted flex items-center justify-center mx-auto mb-3 text-2xl">🤖</div>
                    )}
                    <h3 className="font-semibold text-foreground text-sm truncate">{bot.name}</h3>
                    <Badge variant="outline" className="capitalize text-[10px] mt-1.5">{bot.category}</Badge>
                  </div>
                </Card>
              </motion.div>
            ))}
          </div>

          {/* Comparison Rows */}
          <div className="space-y-1">
            {/* Rating */}
            <CompareRow label="Rating" icon={<Star className="w-4 h-4 text-yellow-500" />} colCount={colCount}>
              {comparisonList.map(bot => (
                <div key={bot.id} className="flex items-center justify-center gap-1.5">
                  <Star className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                  <span className="font-bold text-foreground">{bot.rating}</span>
                </div>
              ))}
            </CompareRow>

            {/* Users */}
            <CompareRow label="Users" icon={<Users className="w-4 h-4 text-primary" />} colCount={colCount}>
              {comparisonList.map(bot => (
                <span key={bot.id} className="font-semibold text-foreground">{(bot.reviewCount / 1000).toFixed(1)}k</span>
              ))}
            </CompareRow>

            {/* Pricing */}
            <CompareRow label="Pricing" icon={<DollarSign className="w-4 h-4 text-primary" />} colCount={colCount}>
              {comparisonList.map(bot => (
                <Badge key={bot.id} variant={bot.price === 'Free' ? 'secondary' : 'outline'} className="text-xs">
                  {bot.price}
                </Badge>
              ))}
            </CompareRow>

            {/* Description */}
            <CompareRow label="Description" icon={<TrendingUp className="w-4 h-4 text-primary" />} colCount={colCount} tall>
              {comparisonList.map(bot => (
                <p key={bot.id} className="text-sm text-muted-foreground leading-relaxed">{bot.description}</p>
              ))}
            </CompareRow>

            {/* Feature Checklist */}
            {allFeatures.length > 0 && (
              <>
                <div className="pt-4 pb-2">
                  <h3 className="text-sm font-semibold text-foreground flex items-center gap-2">
                    <Zap className="w-4 h-4 text-primary" />
                    Feature Comparison
                  </h3>
                </div>
                {allFeatures.map(feature => (
                  <CompareRow key={feature} label={feature} colCount={colCount} compact>
                    {comparisonList.map(bot => {
                      const has = bot.key_features?.includes(feature);
                      return (
                        <div key={bot.id} className="flex justify-center">
                          {has ? (
                            <CheckCircle2 className="w-5 h-5 text-green-500" />
                          ) : (
                            <XCircle className="w-5 h-5 text-muted-foreground/30" />
                          )}
                        </div>
                      );
                    })}
                  </CompareRow>
                ))}
              </>
            )}

            {/* Pros */}
            <div className="pt-4 pb-2">
              <h3 className="text-sm font-semibold text-green-600 dark:text-green-400 flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4" />
                Pros
              </h3>
            </div>
            <div className="grid gap-4" style={{ gridTemplateColumns: `180px repeat(${colCount}, 1fr)` }}>
              <div />
              {comparisonList.map(bot => (
                <div key={bot.id} className="space-y-1.5 py-3 px-3 rounded-lg bg-green-500/5 border border-green-500/10">
                  {bot.pros && bot.pros.length > 0 ? bot.pros.map((pro, idx) => (
                    <div key={idx} className="flex items-start gap-2 text-sm">
                      <CheckCircle2 className="w-3.5 h-3.5 text-green-500 flex-shrink-0 mt-0.5" />
                      <span className="text-foreground">{pro}</span>
                    </div>
                  )) : (
                    <span className="text-sm text-muted-foreground italic">No pros listed</span>
                  )}
                </div>
              ))}
            </div>

            {/* Cons */}
            <div className="pt-4 pb-2">
              <h3 className="text-sm font-semibold text-red-600 dark:text-red-400 flex items-center gap-2">
                <XCircle className="w-4 h-4" />
                Cons
              </h3>
            </div>
            <div className="grid gap-4" style={{ gridTemplateColumns: `180px repeat(${colCount}, 1fr)` }}>
              <div />
              {comparisonList.map(bot => (
                <div key={bot.id} className="space-y-1.5 py-3 px-3 rounded-lg bg-red-500/5 border border-red-500/10">
                  {bot.cons && bot.cons.length > 0 ? bot.cons.map((con, idx) => (
                    <div key={idx} className="flex items-start gap-2 text-sm">
                      <XCircle className="w-3.5 h-3.5 text-red-500 flex-shrink-0 mt-0.5" />
                      <span className="text-foreground">{con}</span>
                    </div>
                  )) : (
                    <span className="text-sm text-muted-foreground italic">No cons listed</span>
                  )}
                </div>
              ))}
            </div>

            {/* Actions */}
            <div className="pt-6">
              <div className="grid gap-4" style={{ gridTemplateColumns: `180px repeat(${colCount}, 1fr)` }}>
                <div />
                {comparisonList.map(bot => (
                  <div key={bot.id} className="flex flex-col gap-2">
                    {bot.preview_url && (
                      <Button size="sm" variant="outline" className="w-full" asChild>
                        <a href={bot.preview_url} target="_blank" rel="noopener noreferrer">
                          Try Demo
                        </a>
                      </Button>
                    )}
                    <Button size="sm" className="w-full bg-primary text-primary-foreground" asChild>
                      <a href={bot.site_url || bot.website} target="_blank" rel="noopener noreferrer">
                        <ExternalLink className="w-3 h-3 mr-1" />
                        Visit Site
                      </a>
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Add More */}
      {comparisonList.length < 5 && (
        <div className="mt-10 text-center">
          <p className="text-muted-foreground text-sm mb-3">
            Compare up to 5 bots for deeper insights.
          </p>
          <Button variant="outline" onClick={() => window.location.href = '/'} size="sm">
            <Sparkles className="w-4 h-4 mr-2" />
            Add More Bots
          </Button>
        </div>
      )}
    </div>
  );
};

/* Reusable comparison row */
const CompareRow: React.FC<{
  label: string;
  icon?: React.ReactNode;
  colCount: number;
  children: React.ReactNode;
  tall?: boolean;
  compact?: boolean;
}> = ({ label, icon, colCount, children, tall, compact }) => (
  <div 
    className={`grid gap-4 items-center border-b border-border/40 ${compact ? 'py-2' : tall ? 'py-4' : 'py-3'}`}
    style={{ gridTemplateColumns: `180px repeat(${colCount}, 1fr)` }}
  >
    <div className="flex items-center gap-2 text-sm text-muted-foreground font-medium truncate">
      {icon}
      <span className="truncate">{label}</span>
    </div>
    {children}
  </div>
);
